import React, { useCallback, useEffect, useMemo, useState } from 'react';
import deltaService from '../../services/deltaService';
import { NEXT_STEPS } from '../../content/deck';
import { Chip, Empty, Section } from './DeltaUI';
import { Footnote, SlideHead, StatementBar, StepRail } from './DeckUI';

// Execution — the deck's closing slide, and then what has actually happened.
//
// The eight steps are the plan as presented and do not move. Everything below them is read
// live: gates are evaluated server-side against the register on every load, and the
// now/next list is derived from the actions' own priority rather than kept as a second
// copy. A hand-maintained progress list beside a real one drifts within weeks, and then
// neither can be trusted.
//
// There is deliberately no percentage-complete figure. Eight steps of wildly different
// size do not average into a number that means anything, and a progress bar that says 38%
// invites a conversation about the bar instead of about the gate that is not met.

const PRIORITY_CHIP = {
  now: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
  next: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  later: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
};

const CHECK_CHIP = {
  met: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  not_met: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
  manual: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
};

const CHECK_LABEL = { met: 'Met', not_met: 'Not met', manual: 'Attestation' };

export default function ExecutionPage() {
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    deltaService.getRoadmap().then(setRoadmap).catch(() => setRoadmap(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const objectives = useMemo(() => roadmap?.objectives || [], [roadmap]);

  // Flattened once, carrying the objective each action came from — an action without its
  // objective is an instruction with no reason attached.
  const actions = useMemo(
    () => objectives.flatMap((o) => (o.actions || []).map((a) => ({ ...a, objective: o.title }))),
    [objectives],
  );
  const now = actions.filter((a) => a.priority === 'now');
  const next = actions.filter((a) => a.priority === 'next');

  return (
    <div>
      <SlideHead headline={NEXT_STEPS.headline} />

      <StatementBar label={NEXT_STEPS.approval.label} title={NEXT_STEPS.approval.title} tone="navy">
        <p>{NEXT_STEPS.approval.body}</p>
        <p className="italic text-gray-500 dark:text-gray-400">{NEXT_STEPS.approval.note}</p>
      </StatementBar>

      <StepRail label={NEXT_STEPS.sequenceLabel} steps={NEXT_STEPS.steps} numbered />

      <div className="mt-6">
        <Section title="Gates"
                 subtitle="Checked against the register on every load, never assumed"
                 onRerun={load} busy={loading}>
          {!roadmap?.gates?.length ? (
            <Empty>{loading ? 'Loading…' : 'No gates defined.'}</Empty>
          ) : (
            <div className="space-y-3">
              {roadmap.gates.map((g) => (
                <div key={g.gate_id} className="rounded border border-gray-200 p-3 dark:border-gray-800">
                  <div className="mb-1.5 flex items-start gap-2">
                    <span className="min-w-0 flex-1 text-[12px] leading-snug text-gray-700 dark:text-gray-300">
                      {g.criteria}
                    </span>
                    <Chip className={g.approved ? CHECK_CHIP.met : g.met ? CHECK_CHIP.manual : CHECK_CHIP.not_met}>
                      {g.approved ? 'Approved' : g.met ? 'Criteria met' : 'Open'}
                    </Chip>
                  </div>
                  <ul className="space-y-1">
                    {g.checks.map((c) => (
                      <li key={c.key} className="flex items-start gap-2 text-[12px]">
                        <Chip className={CHECK_CHIP[c.state]}>{CHECK_LABEL[c.state]}</Chip>
                        <span className="min-w-0 flex-1 text-gray-600 dark:text-gray-400">{c.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Now" subtitle={`${now.length} ${now.length === 1 ? 'action' : 'actions'} in flight`}>
          <ActionList rows={now} loading={loading} empty="Nothing is flagged as immediate." />
        </Section>

        <Section title="Next" subtitle={`${next.length} queued`} defaultOpen={false}>
          <ActionList rows={next} loading={loading} empty="Nothing queued." />
        </Section>

        {objectives.map((o) => (
          <Section key={o.id} title={`${o.sequence}. ${o.title}`} subtitle={o.description} defaultOpen={false}>
            <ActionList rows={o.actions || []} loading={loading} empty="No actions recorded." hideObjective />
          </Section>
        ))}
      </div>

      <Footnote>{NEXT_STEPS.footnote}</Footnote>
    </div>
  );
}

function ActionList({ rows, loading, empty, hideObjective = false }) {
  if (!rows.length) return <Empty>{loading ? 'Loading…' : empty}</Empty>;
  return (
    <ul className="divide-y divide-gray-100 dark:divide-gray-800">
      {rows.map((a) => (
        <li key={a.id} className="py-2.5 first:pt-0 last:pb-0">
          <div className="flex items-start gap-2">
            <Chip className={`mt-0.5 shrink-0 ${PRIORITY_CHIP[a.priority]}`}>
              {a.priority === 'now' ? 'Now' : a.priority === 'next' ? 'Next' : 'Later'}
            </Chip>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] leading-snug text-gray-900 dark:text-gray-100">{a.title}</div>
              {a.detail && (
                <div className="mt-0.5 text-[11px] leading-snug text-gray-500 dark:text-gray-400">{a.detail}</div>
              )}
              <div className="mt-1 flex flex-wrap gap-x-3 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                {!hideObjective && a.objective && <span className="normal-case tracking-normal">{a.objective}</span>}
                <span>{a.due_label || 'No date'}</span>
                <span>{a.owner_label || 'Unassigned'}</span>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
